// --------------------------------------------------------------
// Created On: 2024-03-15
// Author: Zachary Thomas
//
// Last Modified: 2025-11-19
// Modified By: Hannah Vaughan
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import {
  SVG_MAX_PIXEL_SHIFT_WHEN_PANNING,
  SVG_MAX_SIZE_WHEN_ZOOMING,
  SVG_MIN_SIZE_WHEN_ZOOMING,
  SVG_PIXEL_SHIFT_WHEN_PANNING,
  SVG_SIZE_SHIFT_WHEN_ZOOMING,
  SVG_PRINT_DELAY_MILLISECONDS,
} from "../../../constants/miscellaneous";
import Spinner from "../../../components/Spinner/Spinner";
import { ReactSVG } from "react-svg";
import { BOM_SVG_SCRIPT } from "../../../constants/svgScripts";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import SvgPrintablePage from "./SvgPrintablePage/SvgPrintablePage";
import styles from "../BomSvgExplorer/BomSvgExplorer.module.scss";

// Interface that shows interactive drawings of parts.
// Clicking on a subassembly returns information needed to handle navigation to next subassembly.
export default function BomSvgExplorer(props: Props): Component {
  const [printableSvgFilePaths, setPrintableSvgFilePaths] = useState<string[]>([]);
  const [horizontalSvgOffset, setHorizontalSvgOffset] = useState<number>(0);
  const [verticalSvgOffset, setVerticalSvgOffset] = useState<number>(0);
  const [svgZoomLevel, setSvgZoomLevel] = useState<number>(1);
  const [currentlyPrinting, setCurrentlyPrinting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const contentToPrint = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    documentTitle: "Exploded Bill of Materials",
    onAfterPrint: () => setCurrentlyPrinting(false),
  });

  // Apply hover highlighting to selected part number.
  useEffect(() => applyHighlightingToPart(props.hoveredPartNumber), [props.hoveredPartNumber]);

  // Setup an observer to watch for changes in the HTML element that stores the currently hovered part.
  useEffect(() => {
    const hoveredPartHtmlElement = document.getElementById("bom-svg-hovered-part");
    if (hoveredPartHtmlElement !== null) {
      const newMutationObserver = new MutationObserver(() => handlePartHover(hoveredPartHtmlElement));
      newMutationObserver.observe(hoveredPartHtmlElement, {
        attributes: false,
        subtree: false,
        childList: true,
        characterData: false,
      });
      return () => {
        newMutationObserver.disconnect();
      };
    }
  }, []);

  // Keep track of the complete list of printable SVGs for the current BOM.
  useEffect(() => {
    // Get all image file paths.
    let svgFilePaths: string[] = [];
    if (props.bomPath.length > 0) {
      svgFilePaths.push(`/svgs/${props.bomPath[0]}.svg`);
      svgFilePaths.push(`/svgs/${props.bomPath[0]} X-Section.svg`);
    }
    if (props.bomParts !== null) {
      const childSvgFilePaths = getSvgFilePaths(props.bomParts);
      svgFilePaths = [...svgFilePaths, ...childSvgFilePaths];
    }
    const uniqueSvgFilePaths: Set<string> = new Set(svgFilePaths);
    setPrintableSvgFilePaths([...uniqueSvgFilePaths]);
  }, [JSON.stringify(props.bomPath), JSON.stringify(props.bomParts)]);

  // Only render the SVGs that are displayed for printing right before we print. This helps improve performance and it
  // prevents accidental hover highlighting from showing up on the printable version of the SVGs.
  useEffect(() => {
    let newTimerId: number;
    if (currentlyPrinting) {
      setLoading(true);
      newTimerId = setTimeout(() => handlePrint(), SVG_PRINT_DELAY_MILLISECONDS);
    }
    return () => {
      setLoading(false);
      clearTimeout(newTimerId);
    };
  }, [currentlyPrinting]);

  // Get all SVG file paths that exist within an array of parts and their children.
  function getSvgFilePaths(parts: Part[]): string[] {
    let svgFilePaths: string[] = [];
    parts.forEach((part) => {
      if (part.parts.length > 0) {
        svgFilePaths.push(`/svgs/${part.partNumber}.svg`);
        svgFilePaths.push(`/svgs/${part.partNumber} X-Section.svg`);
        const childSvgFilePaths = getSvgFilePaths(part.parts);
        svgFilePaths = [...svgFilePaths, ...childSvgFilePaths];
      }
    });
    return svgFilePaths;
  }

  // Handles changes made to the SVG's current position or zoom level.
  function handleSvgZoomAndPan(
    horizontalOffset: number | null,
    verticalOffset: number | null,
    zoomLevel: number | null
  ): void {
    if (horizontalOffset !== null && Math.abs(horizontalOffset) < SVG_MAX_PIXEL_SHIFT_WHEN_PANNING) {
      setHorizontalSvgOffset(horizontalOffset);
    }
    if (verticalOffset !== null && Math.abs(verticalOffset) < SVG_MAX_PIXEL_SHIFT_WHEN_PANNING) {
      setVerticalSvgOffset(verticalOffset);
    }
    if (zoomLevel !== null && zoomLevel < SVG_MAX_SIZE_WHEN_ZOOMING && zoomLevel > SVG_MIN_SIZE_WHEN_ZOOMING) {
      setSvgZoomLevel(zoomLevel);
    }
  }

  // Apply hover highlighting to selected part number.
  function applyHighlightingToPart(partNumber: string | null): void {
    if (partNumber !== null) {
      const partNumberElements = document.querySelectorAll(`[onmousemove*="\\"${partNumber}\\")"]`);
      const truncatedPartNumber = partNumber.split("-")[0];
      const truncatedPartNumberElements = document.querySelectorAll(`[onmousemove*="\\"${truncatedPartNumber}\\")"]`);
      if (truncatedPartNumberElements.length > 0) {
        const currentHoveredElement = truncatedPartNumberElements[0];
        currentHoveredElement.setAttribute("opacity", "0.5");
      } else if (partNumberElements.length > 0) {
        const currentHoveredElement = partNumberElements[0];
        currentHoveredElement.setAttribute("opacity", "0.5");
      }
    }
  }

  // Pass the current hovered part number to onHover.
  function handlePartHover(hoveredPartHtmlElement: HTMLElement): void {
    if (hoveredPartHtmlElement.innerHTML === "") {
      props.onHover(null);
    } else {
      props.onHover(hoveredPartHtmlElement.innerHTML);
    }
  }

  // Dynamically manipulate some details of the SVG to change how it looks and is interacted with.
  function dynamicSvgModification(svg: SVGSVGElement): void {
    svg.setAttribute("style", "width: 100%;height: 100%;");
    // Modify the embedded scripts for the SVG.
    if (svg.children.length >= 2) {
      const svgScript = svg.children[1];
      svgScript.setAttribute("id", "embedded-svg-script");
      svgScript.innerHTML = BOM_SVG_SCRIPT;
    }
  }

  // Handles click events in the SVG by either navigating or by propagating the click.
  function handleClick(event: React.MouseEvent<SVGSVGElement, MouseEvent>): void {
    // Prevent actually redirecting to a different page if we click on a link.
    event.preventDefault();
    // Traverse the SVG to get the child SVG URL.
    let selectedPartNumber: string | null = null;
    const ownerDocument = (event.target as Element).ownerDocument;
    if (ownerDocument !== null && ownerDocument !== undefined) {
      const activeElement = ownerDocument.activeElement;
      if (activeElement !== null && activeElement !== undefined) {
        const attributes = activeElement.attributes;
        if (attributes !== null && attributes !== undefined) {
          for (let i = 0; i < attributes.length; i++) {
            const currentAttribute = attributes[i];
            if (currentAttribute.name === "xlink:href") {
              if (currentAttribute.nodeValue !== null) {
                selectedPartNumber = currentAttribute.nodeValue.split(".svg")[0];
                break;
              }
            }
          }
        }
      }
    }
    // If we clicked on a valid part for navigation, attempt to navigate.
    if (selectedPartNumber !== null) {
      props.onNavigate([...props.bomPath, selectedPartNumber]);
    } else {
      // If we couldn't navigate with the clicked element, see if it is a sellable part that we can register a click on.
      const parentElement = (event.target as Element).parentElement;
      if (parentElement !== null && parentElement.outerHTML !== null) {
        const outerHtmlArray = parentElement.outerHTML.split("&quot;");
        if (outerHtmlArray.length === 3) {
          props.onClick(outerHtmlArray[1]);
        }
      }
    }
  }

  return (
    <div className={`${styles.body} card vh-75 h-100 p-1`} data-test="bom-svg-explorer">
      <div className="row">
        <div className={props.svgFound ? "col-6" : "col"}>
          <div className="m-3">
            <Breadcrumbs bomPath={props.bomPath} onNavigate={(partNumber) => props.onNavigate(partNumber)} />
          </div>
        </div>
        {props.svgFound && (
          <div className="col-6">
            <button
              data-test="move-svg-right-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => handleSvgZoomAndPan(horizontalSvgOffset - SVG_PIXEL_SHIFT_WHEN_PANNING, null, null)}
            >
              <i className="fa fa-fw fa-arrow-right" />
            </button>
            <button
              data-test="move-svg-left-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => handleSvgZoomAndPan(horizontalSvgOffset + SVG_PIXEL_SHIFT_WHEN_PANNING, null, null)}
            >
              <i className="fa fa-fw fa-arrow-left" />
            </button>
            <button
              data-test="move-svg-up-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => handleSvgZoomAndPan(null, verticalSvgOffset + SVG_PIXEL_SHIFT_WHEN_PANNING, null)}
            >
              <i className="fa fa-fw fa-arrow-up" />
            </button>
            <button
              data-test="move-svg-down-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => handleSvgZoomAndPan(null, verticalSvgOffset - SVG_PIXEL_SHIFT_WHEN_PANNING, null)}
            >
              <i className="fa fa-fw fa-arrow-down" />
            </button>
            <button
              data-test="zoom-out-svg-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => handleSvgZoomAndPan(null, null, svgZoomLevel - SVG_SIZE_SHIFT_WHEN_ZOOMING)}
            >
              <i className="fa fa-fw fa-search-minus" />
            </button>

            <button
              data-test="zoom-in-svg-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => handleSvgZoomAndPan(null, null, svgZoomLevel + SVG_SIZE_SHIFT_WHEN_ZOOMING)}
            >
              <i className="fa fa-fw fa-search-plus" />
            </button>

            <button
              data-test="reset-svg-position-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => handleSvgZoomAndPan(0, 0, 1)}
            >
              <i className="fa fa-fw fa-arrows" />
            </button>

            <button
              data-test="print-images-btn"
              className="btn btn-primary float-end m-2"
              onClick={() => setCurrentlyPrinting(true)}
            >
              <i className="fa fa-fw fa-print" />
            </button>
          </div>
        )}

        {/* This element is watched by a MutationObserver so that SVG scripts can store the hovered part number. */}
        <div id="bom-svg-hovered-part" className="d-none" />

        {props.bomPath.length > 0 && props.svgFound && (
          <div className="col-12">
            <Spinner loading={loading} />
            <div className={styles.svgOuterWrapper}>
              <div
                className={styles.svgInnerWrapper}
                style={{ left: horizontalSvgOffset, top: verticalSvgOffset, transform: `scale(${svgZoomLevel})` }}
              >
                <ReactSVG
                  beforeInjection={(svg) => dynamicSvgModification(svg)}
                  afterInjection={() => applyHighlightingToPart(props.hoveredPartNumber)}
                  src={`/svgs/${props.bomPath[props.bomPath.length - 1]}.svg`}
                  className={styles.svg}
                  wrapper="span"
                  onClick={(e) => handleClick(e as React.MouseEvent<SVGSVGElement, MouseEvent>)}
                  onError={() => props.onError()}
                />
              </div>
            </div>
            {currentlyPrinting && (
              <div className={styles.printContent} ref={contentToPrint} data-test="svg-print-target">
                {/* This enforces landscape mode for printing. Writing this just in the SCSS file does not work. */}
                <style type="text/css" media="print">
                  {
                    "@page { size: landscape; }\
                    "
                  }
                </style>
                {printableSvgFilePaths.map((svgPath) => (
                  <SvgPrintablePage
                    key={svgPath}
                    svgPath={svgPath}
                    topLevelBom={props.bomPath.length > 0 ? props.bomPath[0] : ""}
                    topLevelBomDescription={props.bomDescription}
                    topLevelBomWeight={props.bomWeight}
                    parts={props.bomParts || []}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  bomPath: string[];
  bomParts: Part[] | null;
  bomDescription: string;
  bomWeight: number;
  hoveredPartNumber: string | null;
  svgFound: boolean;
  onHover: (partNumber: string | null) => void;
  onClick: (partNumber: string) => void;
  onNavigate: (bomPath: string[]) => void;
  onError: () => void;
}

interface Part {
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  weight: number;
  isSellable: boolean;
  hasOtherMaterial: boolean;
  isImpeller: boolean;
  parts: Part[];
}
